import { CircleCheck, TriangleAlert } from "@icons";

interface FieldMessageProps {
  error?: string | null;
  valid?: string | boolean;
  hint?: string;
  defaultBadge?: string;
  /** Whether the field is currently empty (gates the fallback badge). */
  isEmpty?: boolean;
}

/**
 * The shared message row rendered below a form control. Exactly one of
 * error / valid message / hint / fallback badge shows, in that priority order,
 * so validation, guidance and the empty-state fallback never stack.
 */
export function FieldMessage({
  error,
  valid,
  hint,
  defaultBadge,
  isEmpty = false,
}: FieldMessageProps) {
  if (error) {
    return (
      <div className="flex items-center gap-1.5 mt-[7px] text-[12.5px] leading-snug text-red-500">
        <span className="flex shrink-0">
          <TriangleAlert size={14} />
        </span>
        {error}
      </div>
    );
  }

  if (typeof valid === "string" && valid) {
    return (
      <div className="flex items-center gap-1.5 mt-[7px] text-[12.5px] leading-snug text-green-600">
        <span className="flex shrink-0">
          <CircleCheck size={14} />
        </span>
        {valid}
      </div>
    );
  }

  if (hint) {
    return (
      <div className="mt-[7px] text-[12.5px] leading-snug text-gray-500">
        {hint}
      </div>
    );
  }

  if (defaultBadge && isEmpty) {
    return (
      <div className="flex items-center mt-[7px] text-[12.5px] leading-snug text-gray-500">
        <span className="inline-flex items-center gap-[5px] h-[22px] pl-2 pr-[9px] rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
          <span className="w-[5px] h-[5px] rounded-full bg-gray-400" />
          {defaultBadge}
        </span>
      </div>
    );
  }

  return null;
}

export default FieldMessage;
